import { supabase } from '../lib/supabase';

 // Helper function to convert snake_case to camelCase

 const toCamelCase = (obj) => {

if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;


const converted = {};

for (const key in obj) {

if (Object.prototype.hasOwnProperty.call(obj, key)) {

  const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());

  converted[camelKey] = typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])

? toCamelCase(obj[key]) // Recursively call for nested objects

: obj[key];

}

}

return converted;

 };



 // Helper function to convert camelCase to snake_case

 const toSnakeCase = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const converted = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
            converted[snakeKey] = obj[key];
        }
    }
    return converted;
};

const generateSlug = (businessName) => {
    if (!businessName) return '';
    return businessName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

export const sellerService = {
    /**
     * Check if user already has a seller account
     */
    async checkExistingAccount(email) {
        try {
            const { data: user } = await supabase
                ?.from('users')
                ?.select('id')
                ?.eq('email', email)
                ?.maybeSingle();

            if (!user) return { exists: false, hasSellerAccount: false };

            const { data: sellerAccount, error } = await supabase
                ?.from('seller_accounts')
                ?.select('id')
                ?.eq('user_id', user?.id)
                ?.maybeSingle();

            if (error && error?.code !== 'PGRST116') throw error;

            return {
                exists: true,
                hasSellerAccount: !!sellerAccount,
                userId: user?.id
            };
        } catch (error) {
            console.error('Error checking existing account:', error);
            throw error;
        }
    },

    /**
     * Create a new seller account
     */
    async createAccount(accountData) {
        try {
            const { data: { user } } = await supabase?.auth?.getUser();
            if (!user) throw new Error('Not authenticated');
            
            const businessName = accountData?.businessName;
            const businessSlug = generateSlug(businessName);

            const dbData = {
                user_id: user?.id,
                business_name: businessName,
                business_slug: businessSlug,
                business_email: accountData?.businessEmail,
                business_phone: accountData?.businessPhone || null,
                tier: accountData?.tier || 'entry',
                account_status: 'Active', // Fulfill not-null constraint
                subscription_start_date: new Date()?.toISOString(),
                subscription_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()
            };

            const { data, error } = await supabase?.from('seller_accounts')?.insert(dbData)?.select()?.single();

            if (error) throw error;
            
            // Add 'Seller' role to the user to grant access
            const { data: roleData } = await supabase.from('roles').select('id').eq('name', 'Seller').single();
            if (roleData) {
                await supabase.from('user_roles').insert({
                    user_id: user.id,
                    role_id: roleData.id
                }).select().maybeSingle();
            }

            return toCamelCase(data);
        } catch (error) {
            console.error('Error creating seller account:', error);
            throw error;
        }
    },

/**
* Get a seller account for a given user ID. Defaults to current authenticated user.
*/
async getSellerAccountForUser(userId = null) {
try {
  let effectiveUserId = userId;
  if (!effectiveUserId) {
const { data: { user } } = await supabase?.auth?.getUser();
if (!user) throw new Error('Not authenticated');
effectiveUserId = user?.id;
  }

  const { data, error } = await supabase?.from('seller_accounts')?.select(`
id,
user_id,
business_name,
business_slug,
business_email,
business_phone,
tier,
account_status,
subscription_status,
subscription_start_date,
subscription_end_date,
monthly_fee,
total_products,
total_sales,
rating,
strategic_account_manager_id,
can_self_fulfill,
can_use_suppliers,
created_at,
updated_at,
status,
country,
business_ein,
business_crn,
address_line_1,
address_line_2,
city,
state_province,
postal_code,
bank_name,
bank_routing_number,
bank_account_number,
bank_verification_status,
seller_first_name,
seller_last_name,
storefront_banner_url,
storefront_company_message,
storefront_categories,
storefront_active
  `)?.eq('user_id', effectiveUserId)?.limit(1)?.maybeSingle(); // MODIFIED: to handle multiple seller accounts

  if (error) {
if (error?.code === 'PGRST116') return null; // No account found
throw error;
  }

  return toCamelCase(data);
} catch (error) {
  console.error('Error fetching seller account for user:', error);
  throw error;
}
},

/**
* Get public-facing storefront data for a given seller ID via an Edge Function.
*/
async getPublicStorefrontData(slug) {
    try {
        if (!slug) {
            console.warn('getPublicStorefrontData: Slug is required.');
            throw new Error('Slug is required.');
        }

        const edgeFunctionUrl = `${supabase.supabaseUrl}/functions/v1/get-public-storefront?slug=${slug}`;
        const response = await fetch(edgeFunctionUrl);

        if (response.status === 404) {
            return null;
        }

        if (!response.ok) {
            let errorData = {};
            try {
                errorData = await response.json();
            } catch (jsonError) {
                console.error('getPublicStorefrontData: Failed to parse error response as JSON:', jsonError);
            }
            console.error('getPublicStorefrontData: Edge Function returned an error:', { status: response.status, error: errorData });
            throw new Error(errorData.error || `Failed to fetch public storefront data: ${response.statusText}`);
        }
        
        const data = await response.json();

        const camelCaseData = toCamelCase(data);

        return camelCaseData;
    } catch (error) {
        console.error('Error fetching public storefront data via Edge Function:', error);
        throw error;
    }
},

    async getActiveTermsVersion() {
      const { data, error } = await supabase
          .from('seller_agreements_versions')
          .select('*')
          .eq('agreement_type', 'seller')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
      
      if (error) throw error;
      return data ? toCamelCase(data) : null;
    },

    async getActiveTermsVersionByType(type = 'seller') {
      const { data, error } = await supabase
          .from('seller_agreements_versions')
          .select('*')
          .eq('agreement_type', type)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
      
      if (error) throw error;
      return data ? toCamelCase(data) : null;
    },

    async recordSellerAgreement(userId, versionId) {
      if (!userId || !versionId) return null;
      const { data, error } = await supabase
          .from('seller_agreements')
          .insert({
              user_id: userId,
              version_id: versionId,
              user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown'
          })
          .select()
          .single();

      if (error && error.code !== '23505') { // Ignore unique constraint violation if already agreed
          console.error("Failed to record seller agreement:", error);
          throw error;
      }
      return data;
    },

/**
* Update seller account details for a specific user.
*/
async updateSellerAccountForUser(userId, updates) {
        try {
            if (!userId) throw new Error('User ID is required to update seller account.');

            const { data: existingAccount, error } = await supabase
                ?.from('seller_accounts')
                ?.select('id')
                ?.eq('user_id', userId)
                ?.single();

            if (error || !existingAccount) {
                throw new Error('Seller account not found for the given user ID.');
            }

            const dbUpdates = toSnakeCase(updates);
            dbUpdates.updated_at = new Date()?.toISOString();
            
            if (updates.businessName) {
                dbUpdates.business_slug = generateSlug(updates.businessName);
            }

            const { data: updatedData, error: updateError } = await supabase?.from('seller_accounts')?.update(dbUpdates)?.eq('id', existingAccount.id)?.select()?.single();

            if (updateError) throw updateError;
            return toCamelCase(updatedData);
        } catch (error) {
            console.error('Error updating seller account:', error);
            throw error;
        }
    },

/**
* Change subscription tier
*/
async changeTier(accountId, newTier) {
try {
  const payload = {
    subscription_start_date: new Date()?.toISOString(),
    subscription_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString(),
    updated_at: new Date()?.toISOString()
  };

  if (newTier && newTier.startsWith('repair_')) {
    payload.repair_tier = newTier;
  } else if (newTier && newTier.startsWith('wrap_')) {
    payload.wrap_tier = newTier;
  } else {
    payload.tier = newTier;
  }

  const { data, error } = await supabase?.from('seller_accounts')?.update(payload)?.eq('id', accountId)?.select()?.single();

  if (error) throw error;
  return toCamelCase(data);
} catch (error) {
  console.error('Error changing tier:', error);
  throw error;
}
},

/**
* Get subscription history
*/
async getSubscriptionHistory(accountId) {
try {
  const { data, error } = await supabase?.from('subscription_history')?.select('*')?.eq('seller_account_id', accountId)?.order('created_at', { ascending: false });

  if (error) throw error;
  return data?.map(toCamelCase);
} catch (error) {
  console.error('Error fetching subscription history:', error);
  throw error;
}
},







/**
* Get all seller accounts (admin function)
*/
async getAllAccounts() {
try {
  const { data, error } = await supabase
?.from('seller_accounts')
?.select(`
 *,
 user:users!seller_accounts_user_id_fkey (
id,
email,
first_name,
last_name
 ),
 account_manager:users!seller_accounts_strategic_account_manager_id_fkey (
id,
email,
first_name,
last_name
 )
`)
?.order('created_at', { ascending: false });

  if (error) throw error;
  return { data: data?.map(toCamelCase), error: null };
} catch (error) {
  console.error('Error fetching all seller accounts:', error);
  return { data: null, error };
}
},

/**
* Delete seller account (admin function)
*/
async deleteAccount(accountId) {
try {
  const { error } = await supabase
?.from('seller_accounts')
?.delete()
?.eq('id', accountId);

  if (error) throw error;
  return { success: true, error: null };
} catch (error) {
  console.error('Error deleting seller account:', error);
  throw error;
}
},

    /**
     * Update manufacturing status of an order item
     */
    async updateManufacturingStatus(orderItemId, status) {
        try {
            const { data, error } = await supabase
                ?.from('order_items')
                ?.update({ manufacturing_status: status })
                ?.eq('id', orderItemId)
                ?.select();

            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error updating manufacturing status:', error);
            throw error;
        }
    },

    /**
     * Get total store visits (views) for a seller
     */
    async getStoreVisits(sellerId) {
        if (!sellerId) return 0;
        try {
            // Count from both authenticated and anonymous sessions
            const [authViews, anonViews] = await Promise.all([
                supabase
                    .from('user_login_sessions')
                    .select('id', { count: 'exact', head: true })
                    .eq('session_type', 'Store View')
                    .eq('session_activity', sellerId),
                supabase
                    .from('user_sessions')
                    .select('id', { count: 'exact', head: true })
                    .eq('session_type', 'Store View')
                    .eq('session_activity', sellerId)
            ]);

            return (authViews.count || 0) + (anonViews.count || 0);
        } catch (error) {
            console.error('Error fetching store visits:', error);
            return 0;
        }
    },

    /**
     * Get tier information
     */
getTierInfo(tier) {
const tiers = {
  seller_view_only: {
    name: 'Complimentary Plan',
    price: 0.00,
    features: [
      'Read-access only for existing products',
      'Storefront visibility is deactivated',
      'Uploading new 3D models is paused',
      'Order fulfillment is paused'
    ]
  },
  entry: {
name: 'Creator',
price: 14.99,
features: [
 'Manage your own order fulfillment',
 'Upload and manage your 3D models',
 'Basic analytics dashboard',
 'Standard seller support'
]
  },
  mid_tier: {
name: 'Creator Plus',
price: 24.99,
features: [
 'All Creator features',
 'Third-party supplier fulfillment',
 'Advanced analytics and reporting',
 'Priority seller support',
 'Bulk product & component uploads via CSV'
]
  },
  platinum: {
name: 'Creator Professional',
price: 34.99,
features: [
 'All Creator Plus features',
 'Dedicated Strategic Account Manager',
 'Premium placement in search results',
 'Custom branding options',
 'API access for automation',
 'White-glove onboarding'
]
  },
  repair_view_only: {
    name: 'Complimentary Viewer',
    price: 0.00,
    features: [
      'View-only access to Customer roster',
      'View-only access to Asset/Vehicle registry',
      'View-only access to Service history',
      'Launch 3D subframe visualizer checks',
      'Adding/editing client assets is paused'
    ]
  },
  repair_entry: {
    name: 'Garage Star',
    price: 19.99,
    features: [
      'Up to 100 active customers',
      'Standard 3D CAD diagnostic selector',
      'Live client invoice ledgers',
      'Email client report delivery'
    ]
  },
  repair_mid: {
    name: 'Pro Mechanic',
    price: 39.99,
    features: [
      'Unlimited customer roster',
      'Interactive diagnostic highlights',
      'Physically scannable QR reporting maps',
      'Tab-focus data auto-sync'
    ]
  },
  repair_premium: {
    name: 'Fleet Master',
    price: 79.99,
    features: [
      'All Pro Mechanic features',
      'Dedicated B2B client dashboard access',
      'Direct catalog ordering for replacements',
      'Multi-technician logging permissions',
      'VIP garage onboarding support'
    ]
  },
  wrap_view_only: {
    name: 'Complimentary Viewer',
    price: 0.00,
    features: [
      'View-only access to Customer roster',
      'View-only access to Asset/Vehicle registry',
      'View-only access to Custom wraps saved',
      'Inline 3D subframe livery reviews',
      'Adding/saving new designs is paused'
    ]
  },
  wrap_entry: {
    name: 'Studio Lite',
    price: 24.99,
    features: [
      'Up to 50 custom wraps saved',
      'Symmetrical 3D color wheel painting',
      'Standard satin/glossy roughness presets',
      'Basic client link previews'
    ]
  },
  wrap_mid: {
    name: 'Vinyl Pro',
    price: 49.99,
    features: [
      'Unlimited custom design configurations',
      'Custom graphic decal PNG/JPG uploads',
      'Specular metalness reflection tuning',
      'Automatic canvas texture downscaling'
    ]
  },
  wrap_premium: {
    name: 'Design Studio',
    price: 99.99,
    features: [
      'All Vinyl Pro features',
      'Scannable QR decal livery sharing',
      'Premium carbon & chrome material presets',
      'Advanced 3D environmental lighting'
    ]
  }
};

return tiers?.[tier] || tiers?.entry;
}
 };